import BuyButton from "~/components/product/ProductDetails/BuyButton"
import VariantSelector from "~/components/product/VariantSelector/index"
import OutOfStock from "~/components/product/OutOfStock"
import AddToCartWithQuantitySelector from "~/components/product/AddToCartWithQuantitySelector"
import ShippingSimulation from "~/components/ui/ShippingSimulation"
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice"
import { useOffer } from "~/sdk/useOffer"
import { useVariantPossibilities } from "@decocms/apps-commerce/sdk/useVariantPossibilities"
import { ImageWidget } from "~/types/widgets"
import type { Product } from "@decocms/apps-commerce/types"
import Image from "~/components/ui/Image"
import Icon from "~/components/ui/Icon"
import useCollapsable from "~/components/ui/useCollapsable"
import getMostCheapSKU from "~/sdk/getMostCheapSKU"
import { getFinalPrice } from "~/sdk/price"
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics"
import { sendEvent } from "~/sdk/analytics"
import { toProductItem } from "~/sdk/ga4/transform/toProductItem"
import { useEffect, useId } from "react"
import TimerJS from "~/components/ui/TimerJS"
import { urlify } from "~/sdk/urlify"
import WishlistButton from "~/components/wishlist/WishlistButton"
import StickyBuyBox from "~/components/product/StickyBuyBox"

/**
 * @title Imagem
 */
interface LayoutImage {
    /**
     * @title Altura
     */
    height: number
    /**
     * @title Largura
     */
    width: number
    /**
     * @title Texto Alternativo
     */
    alt: string
    /**
     * @title Imagem
     */
    src: ImageWidget
}

/**
 * @title Botão "Clique e veja!"
 */
interface GotoReviews {
    /**
     * @title Texto
     */
    label: string
    /**
     * @title ID da Seção
     * @description ID da seção de reviews, padrão: konfidency-reviews
     * @default konfidency-reviews
     */
    sectionId: string
    /**
     * @title Título
     * @description Texto que aparece ao passar o mouse sobre o link
     */
    title?: string
}

/**
 * @title Botão de Compra
 */
interface CTA {
    /**
     * @title Texto
     */
    label: string
    /**
     * @title Ícone
     */
    icon: ImageWidget
    /**
     * @title Largura do ícone (em pixels)
     */
    iconWidth: number
    /**
     * @title Altura do ícone (em pixels)
     */
    iconHeight: number
}

const REVIEWS_ANCHOR_ID = 'konfidency-reviews'
const DESCRIPTION_ANCHOR_ID = 'description'

interface Layout {
    /**
     * @title Botão "Clique e veja!"
     */
    gotoReviews: GotoReviews | null
    /**
     * @title Imagem embaixo do botão de comprar
     */
    image: LayoutImage | null
    /**
     * @title Botão de Compra
     */
    cta: CTA | null
}
export interface Props {
    /**
     * @title Produto
     */
    product: Product
    tabelaDeMedidas: string
    layout: Layout
    isMobile: boolean
    /**
     * @ignore
     */
    skuIdUrl?: string
    /**
     * @ignore
     */
    isPartial: boolean
    /**
     * @ignore
     */
    disabledBuyMonthlySubscription: boolean
    /**
     * @ignore
     */
    productName: string
    /**
     * @ignore
     */
    brand?: string
    /**
     * @ignore
     */
    model: string
    /**
     * @ignore
     */
    sku: string
    /**
     * Resumo da descrição já sanitizado no servidor (loader do ProductDetails).
     * @ignore
     */
    shortDescription?: string
}

function ProductInfo(
    {
        product,
        layout,
        tabelaDeMedidas,
        isMobile,
        isPartial,
        skuIdUrl,
        disabledBuyMonthlySubscription,
        productName,
        brand,
        model,
        sku,
        shortDescription,
    }: Props,
) {
    // Preco Pix fixo
    // http://localhost:8000/goze---acelerador-do-prazer-4-em-1-15g-sexy-fantasy-sf4500/p
    const tabelaDeMedidasCollapsible = useCollapsable()
    const { isVariantOf, inProductGroupWithID } = product

    // Fonte de verdade do buy box.
    //
    // Sem SKU escolhida, a coluna anuncia a variante MAIS BARATA (o CTA abre a
    // matriz de quantidades, então o preço é um "a partir de").
    //
    // Com uma SKU escolhida, quem manda é ela. O Fresh marcava essa escolha com
    // `fresh-partial=true` (partial nav do `f-partial`); aqui a troca de
    // variante é SPA e chega como `?skuId=` (VariantLink, DECO-5445). Nos dois
    // casos o loader VTEX já resolveu `product` PARA essa SKU — inclusive quando
    // ela está esgotada, que é justamente o ramo <OutOfStock/> (o "avise-me").
    //
    // Não dá para derivar isso de `hasVariant`: o antigo `getUrlSkuObject`
    // filtrava as variantes por disponibilidade antes do `find`, então devolvia
    // `undefined` exatamente no caso esgotado.
    const dataSource = isPartial || skuIdUrl
        ? product
        : (getMostCheapSKU(product.isVariantOf?.hasVariant ?? []) ?? product)

    // Add null checks before destructuring
    if (!dataSource || typeof dataSource !== 'object') {
        return null // Return early if no valid product data
    }

    const { productID, offers } = dataSource
    const description = product.description || isVariantOf?.description
    const { price = 0, listPrice = 0, seller = '1', installments } = useOffer(offers)

    const currency = offers?.priceCurrency
    const inventoryLevel = offers?.offers[0]?.inventoryLevel.value ?? 0

    const hasVariant = isVariantOf?.hasVariant ?? []
    const possibilities = useVariantPossibilities(hasVariant, product)
    const possibilitiesKeys = Object.keys(possibilities)

    const hasVariants = possibilitiesKeys.length > 0 ||
        (possibilitiesKeys.length === 1 &&
            Object.keys(Object.values(possibilities)[0]).length > 0)

    const isAvailable = offers?.offers[0]?.availability === 'https://schema.org/InStock'

    const { finalPrice, discountType } = getFinalPrice(dataSource.offers)

    // Ajustar no codigo depois dos testes, colocar essa funcao como utilitario
    const bankersRound = (num: number, decimalPlaces: number) => {
        const d = decimalPlaces || 0
        const m = Math.pow(10, d)
        const n = +(d ? num * m : num).toFixed(8) // Avoid rounding errors
        const i = Math.floor(n), f = n - i
        const e = 1e-8 // Allow for rounding errors in f
        const r = (f > 0.5 - e && f < 0.5 + e) ? ((i % 2 === 0) ? i : i + 1) : Math.round(n)
        return d ? r / m : r
    }

    const inCashDiscountInPercentage = finalPrice && listPrice &&
        Math.floor(((listPrice - finalPrice) / listPrice) * 100)

    const discount = price && listPrice ? listPrice - price : 0
    const discountInPercentage = discount && listPrice
        ? Math.floor((discount / listPrice) * 100)
        : 0

    // Sticky buy box (mobile + desktop bar that slides in once the real buy box
    // scrolls past). Flat strings because the bar re-renders the same figures in
    // a much tighter layout than the block above.
    const discountLabel = [
        inCashDiscountInPercentage ? `${inCashDiscountInPercentage}% OFF à vista` : '',
        discountInPercentage ? `${discountInPercentage}% OFF a prazo` : '',
    ]
        .filter(Boolean)
        .join(' | ')
    const installmentsText = installments
        ?.replace(/<[^>]+>/g, '')
        .replace('sem juros', '')
        .trim()
    const stickyImage = (dataSource.image ?? product.image)?.[0]?.url

    const countdown = product.isVariantOf?.additionalProperty.find(({ name }) =>
        name === 'CountDown'
    )?.value
    const countdownExpired = new Date(countdown ?? 0).getTime() < new Date().getTime()

    const timerId = useId()

    const analytics = {
        items: [toProductItem(product, { index: 0, quantity: 1 })],
        view: { id: 'product_page', name: 'Página de produto' },
    }

    // `view_item` sai de um efeito, não do `<SendEventOnView>` (script inline +
    // IntersectionObserver). O script inline só executa quando chega no HTML do
    // SSR: na navegação SPA o React insere o `<script>` sem executá-lo, e na
    // PDP→PDP esta instância nem remonta (mesma rota, só muda o param). Por isso
    // nenhum `view_item` saía fora do acesso direto (#282).
    //
    // A chave é o payload serializado: dispara uma vez por produto/SKU exibido,
    // inclusive PDP→PDP e troca de variante (`?skuId=`), e não repete em
    // re-render com o mesmo produto.
    const viewItemPayload = JSON.stringify(
        toAnalytics({ type: 'view_item', data: analytics }),
    )
    useEffect(() => {
        sendEvent(JSON.parse(viewItemPayload))
    }, [viewItemPayload])

    return (
        <>
            <div>
                {/* Konfidency - selo Top Rated (topo da coluna de infos) */}
                <div
                    className='konfidency-badges w-fit'
                    data-sku={inProductGroupWithID}
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{ __html: '' }}
                />

                <div className='flex xl:gap-x-4 gap-x-3 max-sm:flex-wrap max-sm:gap-y-3 max-lg:text-sm lg:text-xs xl:mb-4 mb-3 mt-2'>
                    {brand && (
                        <>
                            <a href={`/${urlify(brand)}`}>
                                Fabricante: <span>{brand}</span>
                            </a>

                            <hr className='border-black border-l h-auto' />
                        </>
                    )}

                    <span>Cód: {model}</span>

                    <hr className='border-black border-l h-auto max-sm:hidden' />

                    <div className='flex max-sm:w-full sm:min-w-48'>
                        <WishlistButton
                            variant='pdp'
                            productID={productID}
                            sku={sku}
                            analytics={analytics}
                        />
                    </div>
                </div>

                {layout.gotoReviews && (
                    <div className='trustvox-rating [&_.ts-shelf-left]:hidden xl:mb-4 mb-3'>
                        <div className='flex items-center gap-1'>
                            {/* Estrelas: o widget da Konfidency abre o modal de avaliações */}
                            <div
                                className='konfidency-reviews-summary'
                                data-sku={inProductGroupWithID}
                                suppressHydrationWarning
                                dangerouslySetInnerHTML={{ __html: '' }}
                            />
                            <a
                                href={`#${REVIEWS_ANCHOR_ID}`}
                                title={layout.gotoReviews.title ??
                                    'Pergunte e veja opiniões de quem já comprou'}
                            >
                                <span className='rating-click-here !text-red-wine'>
                                    {layout.gotoReviews.label || 'Clique e veja!'}
                                </span>
                            </a>
                        </div>
                    </div>
                )}

                {/* `min-[767px]:leading-[2rem]` reproduz o line-height que o `text-2xl` do
                    Tailwind v3 impunha: lá a regra da media query vinha depois de
                    `.leading-[110%]` na folha e ganhava. No v4 `leading-*` sempre vence,
                    o que deixava o título em 26.4px em vez de 2rem no desktop. */}
                <h1 className='font-black text-base min-[767px]:text-2xl text-black uppercase leading-[110%] min-[767px]:leading-[2rem]'>
                    {productName}
                </h1>

                {description && (
                    <>
                        <div className='text-sm text-black relative max-w-xl mb-[30px]'>
                            <div>
                                <span
                                    className='line-clamp-3 overflow-hidden text-ellipsis'
                                    dangerouslySetInnerHTML={{
                                        __html: shortDescription ?? '',
                                    }}
                                />
                            </div>

                            <a
                                href={`#${DESCRIPTION_ANCHOR_ID}`}
                                className='underline underline-offset-4 inline-flex max-[1025px]:flex-row-reverse items-center gap-1 font-bold text-red-wine'
                                title='Saiba mais'
                            >
                                Ver descrição completa
                                <Icon id='FilledChevronRight' width={10} height={10} />
                            </a>
                        </div>
                    </>
                )}

                {!countdownExpired && (
                    <div className='mb-4'>
                        <span className='p-1.5 leading-none bg-orange text-red-wine font-black text-lg rounded-lg block mb-1 w-fit'>
                            OFERTA EXPIRA EM:
                        </span>
                        <div className='flex gap-1'>
                            <span className='p-2 text-white font-bold text-lg rounded countdown bg-red-wine'>
                                <span id={`${timerId}::days`} />
                            </span>
                            <span className='p-2 text-white font-bold text-lg rounded countdown bg-red-wine'>
                                <span id={`${timerId}::hours`} />
                            </span>
                            <span className='p-2 text-white font-bold text-lg rounded countdown bg-red-wine'>
                                <span id={`${timerId}::minutes`} />
                            </span>
                            <span className='p-2 text-white font-bold text-lg rounded countdown bg-red-wine'>
                                <span id={`${timerId}::seconds`} />
                            </span>
                        </div>
                    </div>
                )}
                <div id='product-buy-box-anchor' className='flex flex-col-reverse lg:flex-col'>
                    {hasVariants && (
                        <div id='product-variations'>
                            <VariantSelector product={product} />
                        </div>
                    )}

                    {price + (listPrice ?? 0) > 0 && (
                        <div>
                            {isAvailable && (
                                <>
                                    {(inCashDiscountInPercentage || discountInPercentage > 0) && (
                                        <div className='rounded bg-orange text-white text-base w-fit px-2 py-0.5 mb-1'>
                                            <span>
                                                {inCashDiscountInPercentage
                                                    ? (
                                                        <>
                                                            <span className='font-bold'>
                                                                {inCashDiscountInPercentage}%
                                                            </span>{' '}
                                                            OFF à vista
                                                            {discountInPercentage > 0 ? ' | ' : ''}
                                                        </>
                                                    )
                                                    : <></>}
                                                {discountInPercentage > 0 && (
                                                    <>
                                                        <span className='font-bold'>
                                                            {discountInPercentage}%
                                                        </span>{' '}
                                                        OFF à prazo
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}

                            {isAvailable && listPrice > finalPrice && (
                                <p className='line-through text-neutral-400 text-xs'>
                                    De: {formatPrice(listPrice, currency)}
                                </p>
                            )}
                            <p className='font-bold text-[22px] text-red-wine leading-none'>
                                {formatPrice(
                                    isAvailable ? bankersRound(finalPrice, 2) : listPrice,
                                    currency,
                                )}
                                {isAvailable && discountType === 'pix' && (
                                    <>
                                        {' '}
                                        <span className='text-lg font-normal'>no pix</span>
                                    </>
                                )}
                            </p>
                            {isAvailable && (
                                <>
                                    <p className='text-sm text-neutral-600 leading-none'>
                                        <span className='font-bold text-xl min-[1025px]:text-base'>
                                            {formatPrice(price, currency)}
                                        </span>{' '}
                                        a prazo
                                    </p>

                                    {installments && (
                                        <div className='text-lg min-[1025px]:text-sm text-neutral-600'>
                                            <p
                                                dangerouslySetInnerHTML={{
                                                    __html: `ou ${
                                                        installments.replace('sem juros', '')
                                                    }`,
                                                }}
                                            />
                                        </div>
                                    )}
                                </>
                            )}

                            {isAvailable && inventoryLevel < 10 && !hasVariants && (
                                <span className='text-[#d40000] font-bold block text-sm'>
                                    {inventoryLevel === 1
                                        ? '🔥 Última unidade'
                                        : `🔥 Restam apenas ${inventoryLevel} unidades`}
                                </span>
                            )}

                            {hasVariants && isAvailable
                                ? (
                                    <BuyButton
                                        text={layout.cta?.label ?? 'COMPRAR'}
                                        icon={layout.cta?.icon}
                                        iconWidth={layout.cta?.iconWidth}
                                        iconHeight={layout.cta?.iconHeight}
                                    />
                                )
                                : isAvailable
                                ? (
                                    <AddToCartWithQuantitySelector
                                        // SPA nav between PDPs reuses this instance; without a
                                        // key the typed quantity leaks into the next product.
                                        key={product.productID}
                                        layout='ProductPage'
                                        icon={layout.cta?.icon}
                                        iconWidth={layout.cta?.iconWidth}
                                        iconHeight={layout.cta?.iconHeight}
                                        product={{
                                            id: product.productID,
                                            quantity: 1,
                                            seller,
                                        }}
                                        isMobile={isMobile}
                                        isPDP
                                        inventoryLevel={inventoryLevel}
                                        analytics={analytics}
                                        disabledBuyMonthlySubscription={disabledBuyMonthlySubscription}
                                    />
                                )
                                : <OutOfStock productID={productID} />}
                        </div>
                    )}
                </div>

                {layout.image && (
                    <Image
                        className='mt-6 mb-12'
                        height={layout.image.height}
                        width={layout.image.width}
                        alt={layout.image.alt}
                        src={layout.image.src}
                    />
                )}

                {tabelaDeMedidas && (
                    <tabelaDeMedidasCollapsible.Collapsable
                        id='measurements-table'
                        className='border-y border-y-[#ccc] py-2 [&:not(:has(input:checked))]:pb-0'
                        open
                    >
                        <tabelaDeMedidasCollapsible.Trigger
                            htmlFor='measurements-table'
                            className='flex items-center gap-x-5 group mb-2 text-sm text-black'
                        >
                            <h4>TAMANHOS E MEDIDAS</h4>

                            <div>
                                <span className='peer-checked:group-[]:hidden block'>+</span>
                                <span className='peer-checked:group-[]:block hidden'>-</span>
                            </div>
                        </tabelaDeMedidasCollapsible.Trigger>
                        <tabelaDeMedidasCollapsible.ContentWrapper className='group'>
                            <tabelaDeMedidasCollapsible.Content>
                                <div
                                    dangerouslySetInnerHTML={{ __html: tabelaDeMedidas }}
                                    className='[&_thead_tr]:bg-[#fff0f7] [&_thead_tr]:uppercase [&_tbody_tr:nth-child(even)]:bg-[#fff0f7] [&_tbody_td]:text-center [&_td]:text-sm [&_td]:p-0 [&_th]:text-sm [&_th]:font-normal [&_thead_th]:font-bold [&_td]:text-black [&_th]:text-black [&_th]:w-20'
                                />
                            </tabelaDeMedidasCollapsible.Content>
                        </tabelaDeMedidasCollapsible.ContentWrapper>
                    </tabelaDeMedidasCollapsible.Collapsable>
                )}

                {/* Shipping Simulation */}
                {isAvailable && (
                    <div className='mt-8'>
                        <ShippingSimulation
                            items={[
                                {
                                    id: Number(product.sku),
                                    quantity: 1,
                                    seller,
                                },
                            ]}
                        />
                    </div>
                )}
            </div>

            {isAvailable && price + (listPrice ?? 0) > 0 && (
                <StickyBuyBox
                    product={{
                        id: product.productID,
                        quantity: 1,
                        seller,
                    }}
                    productName={productName}
                    imageUrl={stickyImage}
                    productGroupId={inProductGroupWithID ?? productID}
                    listPrice={listPrice > finalPrice ? formatPrice(listPrice, currency) : null}
                    finalPrice={formatPrice(bankersRound(finalPrice, 2), currency)}
                    price={formatPrice(price, currency)}
                    discountLabel={discountLabel}
                    paymentMethod={discountType === 'pix' ? 'no pix' : undefined}
                    installments={installmentsText}
                    inventoryLevel={inventoryLevel}
                    hasVariants={hasVariants}
                    analytics={analytics}
                    disabled={disabledBuyMonthlySubscription}
                    icon={layout.cta?.icon}
                    iconWidth={layout.cta?.iconWidth}
                    iconHeight={layout.cta?.iconHeight}
                />
            )}

            {!countdownExpired && <TimerJS rootId={timerId} expiresAt={countdown as string} />}
        </>
    )
}

export default ProductInfo
